import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Grid, Typography, Chip, Divider } from "@mui/material";
import Header from "../../components/Header";
import { getWorshipCenter } from "./../../ApiConfig.jsx";
import { formatStoredTime } from "../../Utils/worshipTime";

const DAYS_LABEL = {
  sunday: "Sunday", monday: "Monday", tuesday: "Tuesday",
  wednesday: "Wednesday", thursday: "Thursday", friday: "Friday", saturday: "Saturday",
};

function ViewWorship() {
  const { id } = useParams();
  const [details, setDetails] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const data = await getWorshipCenter(id);
        if (data.status) setDetails(data.result);
      } catch (err) {
        console.error(err);
      }
    };
    fetch();
  }, [id]);

  if (!details) return null;

  const basicFields = [
    { label: "Church Name",   value: details.church },
    { label: "Address",       value: details.address },
    { label: "Email",         value: details.email },
    { label: "Telephone",     value: details.tel },
    { label: "Description", value: details.description },
    { label: "Pastor",        value: details.paster },
    { label: "Latitude",      value: details.latitude },
    { label: "Longitude",     value: details.longtitude },
    { label: "Status",        value: details.status },
    { label: "Created At",    value: new Date(details.createdAt).toLocaleString() },
  ];

  const openHours = Array.isArray(details.openHours) ? details.openHours : [];
  const services  = Array.isArray(details.service)   ? details.service   : [];

  return (
    <Box m="20px" height="70vh" overflow="auto" paddingRight="20px">
      <Header title={`View Worship Center #${id}`} subtitle="" />

      <Box ml="40px">

        {/* ── Basic Info ── */}
        <Typography variant="h6" fontWeight="700" mb="14px">Basic Information</Typography>
        <Grid container spacing={2} mb="30px">
          {basicFields.map(({ label, value }) => (
            <React.Fragment key={label}>
              <Grid item xs={12} sm={2.5}>
                <Typography variant="h6" fontWeight="bold" color="text.secondary">{label}:</Typography>
              </Grid>
              <Grid item xs={12} sm={9.5}>
                <Typography variant="h6" sx={{ textTransform: label === "Status" ? "capitalize" : "none" }}>
                  {value ?? "—"}
                </Typography>
              </Grid>
            </React.Fragment>
          ))}
        </Grid>

        <Divider sx={{ mb: "24px" }} />

        {/* ── Open Hours ── */}
        <Typography variant="h6" fontWeight="700" mb="14px">Open Hours</Typography>
        {openHours.length === 0 ? (
          <Typography color="text.secondary" mb="24px">No open hours recorded.</Typography>
        ) : (
          <Grid container spacing={1.5} mb="30px">
            {openHours.map(([day, from, to], i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Box
                  sx={{
                    backgroundColor: "rgba(104,112,250,0.08)",
                    borderRadius: "8px",
                    p: "12px 16px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography fontWeight="600">{DAYS_LABEL[day] ?? day}</Typography>
                  <Typography color="text.secondary">
                    {formatStoredTime(from)} – {formatStoredTime(to)}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}

        <Divider sx={{ mb: "24px" }} />

        {/* ── Services ── */}
        <Typography variant="h6" fontWeight="700" mb="14px">Services</Typography>
        {services.length === 0 ? (
          <Typography color="text.secondary" mb="24px">No services recorded.</Typography>
        ) : (
          <Grid container spacing={2} mb="30px">
            {services.map(([day, from, to, namesStr], i) => {
              const names = namesStr ? namesStr.split(",").map((n) => n.trim()).filter(Boolean) : [];
              return (
                <Grid item xs={12} sm={6} key={i}>
                  <Box
                    sx={{
                      backgroundColor: "rgba(104,112,250,0.08)",
                      borderRadius: "8px",
                      p: "14px 16px",
                    }}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb="10px">
                      <Typography fontWeight="600">{DAYS_LABEL[day] ?? day}</Typography>
                      <Typography color="text.secondary" fontSize="0.9rem">
                        {formatStoredTime(from)} – {formatStoredTime(to)}
                      </Typography>
                    </Box>
                    <Box display="flex" flexWrap="wrap" gap="6px">
                      {names.length > 0
                        ? names.map((name, ni) => (
                            <Chip
                              key={ni}
                              label={name}
                              size="small"
                              sx={{
                                backgroundColor: "rgba(104,112,250,0.22)",
                                fontWeight: 500,
                              }}
                            />
                          ))
                        : <Typography variant="caption" color="text.secondary">No service names</Typography>
                      }
                    </Box>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        )}

        <Divider sx={{ mb: "24px" }} />

        {/* ── Image ── */}
        {details.imageURL && (
          <>
            <Typography variant="h6" fontWeight="700" mb="14px">Image</Typography>
            <Box mb="30px">
              <img
                src={details.imageURL}
                alt="Worship Center"
                style={{ maxWidth: "420px", maxHeight: "420px", borderRadius: "10px", objectFit: "cover" }}
              />
            </Box>
          </>
        )}

      </Box>
    </Box>
  );
}

export default ViewWorship;